import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IAttachment } from '@common/interfaces/attachment';
import { ID } from '@common/interfaces/id';
import { Topics } from '@common/interfaces/topics';
import { DangerActionModalComponent } from '@ep-om/project/components/issues/danger-action-modal/danger-action-modal.component';
import { PhotoModalComponent } from '@ep-om/project/components/issues/photo-modal/photo-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { NzModalService } from 'ng-zorro-antd/modal';
import { environment } from 'src/environments/environment';
import { ActionService } from '../action/action.service';
import { CrudService } from '../crudService';
import { ProjectQuery } from '../project/project.query';
import { ProjectEntityStoreStrategy } from '../updateStoreStrategies';
import { AttachmentQuery } from './attachment.query';
import { AttachmentStore } from './attachment.store';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { BehaviorSubject } from 'rxjs';
import { IExportsLink } from '@common/interfaces/excelReport';
import { WsACKCallback } from '@common/api/ack';
import { RealtimeFeatureCommandService } from '@ep-om/realtime-v2';

const WIN_EXE_DDL_STARTER = '4D 5A';
const LINUX_ELF_STARTER = '7F 45 4C 46';

@Injectable({
  providedIn: 'root'
})
export class AttachmentService extends CrudService<IAttachment, AttachmentStore, AttachmentQuery> {
  readonly createAccessToken = environment.apiUrl + '/api/attachment/createToken';
  readonly fileUploadUrl = environment.apiUrl + '/api/attachment';
  readonly photoFileUploadUrl = environment.apiUrl + '/api/attachment/photo';
  public isLoadingAttachment$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  constructor(
    private http: HttpClient,
    protected store: AttachmentStore,
    public query: AttachmentQuery,
    protected actionService: ActionService,
    protected updateStrategy: ProjectEntityStoreStrategy,
    private projectQuery: ProjectQuery,
    private _nzModalService: NzModalService,
    private _nzNotificationService: NzNotificationService,
    private translate: TranslateService,
    private featureCommandService: RealtimeFeatureCommandService,
  ) {
    super(Topics.ATTACHMENT, store, query, actionService, updateStrategy);
  }

  async createAttachment(file: File, issueId: string, interfaceId?: string) {
    return await this.uploadAttachment('file', file, issueId, interfaceId);
  }

  async createPhotoAttachment(photo: File, issueId: string, interfaceId?: string) {
    return await this.uploadAttachment('photo', photo, issueId, interfaceId);
  }

  async uploadAttachment(fileType: string, file: File, issueId: string, interfaceId?: string) {
    const url = fileType === 'photo' ? this.photoFileUploadUrl : this.fileUploadUrl;
    const appendPropName = fileType === 'photo' ? 'photoFile' : 'file';
    const formData = new FormData();
    formData.append('issueId', issueId);
    formData.append('projectId', this.projectQuery.getActiveId() as string);
    formData.append(appendPropName, file, file.name);
    if (interfaceId) formData.append('interfaceId', interfaceId);
    this.isLoadingAttachment$.next(true);
    let res: any;
    try {
      res = await this.http.post<any>(url, formData).toPromise();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const thumbnail = res.createdAttachment.thumbnail || {};
      if (thumbnail.data) res.createdAttachment.thumbnail = res.createdAttachment.thumbnail.data;
      this.store.add(res.createdAttachment);
      this.afterCreation(res.createdAttachment.id, 'issue_link');
    } catch (error) {
      this._nzNotificationService.error('Error', 'Abbiamo riscontrato un errore durante la creazione del file');
    }
    this.isLoadingAttachment$.next(false);
    return res;
  }

  downloadAttachment(attachment: IAttachment) {
    attachment.thumbnail
      ? this.downloadPhotoById(attachment.copyOf || attachment.cloneOf || attachment.id)
      : this.downloadAttachmentById(attachment.copyOf || attachment.cloneOf || attachment.id);
  }

  downloadAttachmentById(id: ID) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/octet-stream',
      Accept: 'application/octet-stream'
    });
    return this.http.get(this.fileUploadUrl + '/' + id, { headers, responseType: 'arraybuffer', observe: 'response' }).subscribe((res) => {
      let blob = new Blob([res.body]);
      let fileName = res.headers.get('content-disposition').split(';')[1].split('filename')[1].split('=')[1].trim();
      fileName = fileName.substring(1, fileName.length - 1);
      saveAs(blob, fileName);
    });
  }

  downloadPhotoById(attachmentId: string) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/octet-stream',
      Accept: 'application/octet-stream'
    });
    return this.http
      .get(this.photoFileUploadUrl + '/' + attachmentId, { headers, responseType: 'arraybuffer', observe: 'response' })
      .subscribe((res) => {
        let blob = new Blob([res.body]);
        let fileName = res.headers.get('content-disposition').split(';')[1].split('filename=').slice(1).join('filename=').trim().slice(1, -1);
        // Il valore di content-disposition è una stringa -> "attachment; filename=\"<nomeFile>.png\""
        saveAs(blob, fileName);
      });
  }

  async getPhotoById(attachmentId: string) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/octet-stream',
      Accept: 'application/octet-stream'
    });
    const res = await this.http
      .get(this.photoFileUploadUrl + '/' + attachmentId, { headers, responseType: 'arraybuffer', observe: 'response' })
      .toPromise();
    return this.bufferToBase64(res.body);
  }

  bufferToBase64(buffer: Buffer | ArrayBuffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = `data:image/jpeg;base64,${window.btoa(binary)}`;

    return base64;
  }

  async openPhotoModal(attachment: IAttachment, canDelete: boolean) {
    const res = await this.openAttachmentModal(attachment, canDelete);
    if (res && res['message'] && res['message'] === 'remove') {
      this.trash(attachment.readonly, attachment.id);
    }
  }

  async openAttachmentModal(attachment: IAttachment, canDelete: boolean) {
    const modalRef = this._nzModalService.create({
      nzContent: PhotoModalComponent,
      nzWidth: '100%',
      nzClassName: 'photo-modal',
      nzClosable: false,
      nzFooter: null,
      nzComponentParams: {
        issueId: attachment.issueId,
        attachment: attachment,
        canDelete
      }
    });
    const res = await modalRef.afterClose.toPromise();
    return res;
  }

  async openDeleteModal(attachment: IAttachment) {
    return new Promise<boolean>((resolve, reject) => {
      const modalRef = this._nzModalService.create({
        nzContent: DangerActionModalComponent,
        nzWidth: '100%',
        nzClassName: 'alert-modal',
        nzClosable: false,
        nzFooter: null,
        nzOnOk: () => resolve(true),
        nzComponentParams: {
          actionName: this.translate.instant('ATTACHMENTS.DELETE_MODAL.MODAL_ACTION'),
          question: this.translate.instant('ATTACHMENTS.DELETE_MODAL.MODAL_QUESTION'),
          info: this.translate.instant('ATTACHMENTS.DELETE_MODAL.MODAL_INFO'),
          issueId: attachment.issueId
        }
      });
    });
  }

  async openConfirmUploadModal({ sizeLimit }) {
    return new Promise<boolean>((resolve, reject) => {
      const modalRef = this._nzModalService.create({
        nzContent: DangerActionModalComponent,
        nzWidth: '100%',
        nzClassName: 'alert-modal',
        nzClosable: false,
        nzFooter: null,
        nzOnOk: () => resolve(true),
        nzComponentParams: {
          actionName: this.translate.instant('ATTACHMENTS.UPLOAD_MODAL.MODAL_ACTION'),
          question: this.translate.instant('ATTACHMENTS.UPLOAD_MODAL.MODAL_QUESTION', { fileSizeLimit: sizeLimit }),
          info: this.translate.instant('ATTACHMENTS.UPLOAD_MODAL.MODAL_INFO')
        }
      });
    });
  }

  getFileIconData(ext: string) {
    let iconExt: string = '';
    let iconColor: string = '';
    switch (ext.toLowerCase()) {
      case 'txt':
        iconExt = 'text';
        iconColor = '';
        break;
      case 'jpg':
      case 'jpeg':
        iconExt = 'jpg';
        iconColor = '';
        break;
      case 'pdf':
        iconExt = 'pdf';
        iconColor = 'red';
        break;
      case 'gif':
      case 'png':
        iconExt = 'image';
        iconColor = '';
        break;
      case 'zip':
        iconExt = 'zip';
        iconColor = '';
        break;
      case 'doc':
      case 'docx':
        iconExt = 'word';
        iconColor = 'blue';
        break;
      case 'xls':
      case 'xlsx':
        iconExt = 'excel';
        iconColor = 'green';
        break;
      default:
        iconExt = 'unknown';
        iconColor = '';
        break;
    }
    return { iconExt, iconColor };
  }

  async showFile(attachment: IAttachment, fileExt: string) {
    const openUrl = await this.http.post<IExportsLink>(`${this.fileUploadUrl}/createOpenLink`, attachment).toPromise();
    if (!openUrl) return;
    const baseUrl = `${this.fileUploadUrl}/link/${openUrl.uid}/${attachment.copyOf || attachment.cloneOf || attachment.id}`;
    const excel_csv = `ms-excel:ofv|u|${baseUrl}`;
    let docUrl: string;
    switch (fileExt) {
      case 'pdf':
      case 'txt':
        docUrl = baseUrl;
        break;
      case 'doc':
      case 'docx':
      case 'xls':
      case 'xlsx':
      case 'csv':
        docUrl = excel_csv;
        break;
    }
    const link = document.createElement('a');
    link.href = docUrl;
    link.target = '_blank';
    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  }

  isAttachmentLoading() {
    return this.isLoadingAttachment$;
  }

  trash(readonly: boolean, attachmentId: ID, callback?: WsACKCallback<void>) {
    if (readonly) {
      this._nzNotificationService.error('Error', this.translate.instant('ATTACHMENTS.CANNOT_DELETE'));
      return false;
    }
    const dto = { attachmentId };
    this.featureCommandService.send<void>(
      {
        target: 'feature:attachment',
        command: 'trash',
        dto,
        ...this.projectIdFromAttachment(attachmentId),
      },
      () => this.actionService.sendMessage(Topics.ATTACHMENT, 'trash', dto, callback),
      callback,
    );
  }

  trashForLinkedProject(attachmentId: ID, projectId: string, callback?: WsACKCallback<void>) {
    const dto = { attachmentId, projectId };
    this.featureCommandService.send<void>(
      {
        target: 'feature:attachment',
        command: 'trash-linked-project',
        dto,
        ...this.projectIdFromAttachment(attachmentId),
      },
      () => this.actionService.sendMessage(Topics.ATTACHMENT, 'trashLinkedByProject', dto, callback),
      callback,
    );
  }

  afterCreation(attachmentId: string, typeInterfaceName: string, callback?: WsACKCallback<void>) {
    const dto = { attachmentId, typeInterfaceName };
    this.featureCommandService.send<void>(
      {
        target: 'feature:attachment',
        command: 'after-creation',
        dto,
        ...this.projectIdFromAttachment(attachmentId),
      },
      () => this.actionService.sendMessage(Topics.ATTACHMENT, 'afterCreation', dto, callback),
      callback,
    );
  }

  /*   copyToLinked(attachmentId: string, typeInterfaceName: string, callback?: WsACKCallback<void>) {
      this.actionService.sendMessage(Topics.ATTACHMENT, 'copyToLinked', { attachmentId, typeInterfaceName }, callback);
    }
  
    copyToLinkedBy(attachmentId: string, typeInterfaceName: string, callback?: WsACKCallback<void>) {
      this.actionService.sendMessage(Topics.ATTACHMENT, 'copyToLinkedBy', { attachmentId, typeInterfaceName }, callback);
    } */

  copyToLinkedProject(attachmentId: string, projectId: string, typeInterfaceName: string, callback?: WsACKCallback<void>) {
    const dto = { attachmentId, typeInterfaceName, projectId };
    this.featureCommandService.send<void>(
      {
        target: 'feature:attachment',
        command: 'copy-to-linked-project',
        dto,
        ...this.projectIdFromAttachment(attachmentId),
      },
      () => this.actionService.sendMessage(Topics.ATTACHMENT, 'copyToLinkedProject', dto, callback),
      callback,
    );
  }

  copyToLinkedByProject(attachmentId: string, projectId: string, typeInterfaceName: string, callback?: WsACKCallback<void>) {
    const dto = { attachmentId, typeInterfaceName, projectId };
    this.featureCommandService.send<void>(
      {
        target: 'feature:attachment',
        command: 'copy-to-linked-by-project',
        dto,
        ...this.projectIdFromAttachment(attachmentId),
      },
      () => this.actionService.sendMessage(Topics.ATTACHMENT, 'copyToLinkedByProject', dto, callback),
      callback,
    );
  }

  getAllByCopyOf(attachmentId: string, callback?: WsACKCallback<IAttachment[]>) {
    const dto = { attachmentId };
    this.featureCommandService.send<IAttachment[]>(
      {
        target: 'feature:attachment',
        command: 'get-all-by-copy-of',
        dto,
        ...this.projectIdFromAttachment(attachmentId),
      },
      () => this.actionService.sendMessage(Topics.ATTACHMENT, 'getAllByCopyOf', dto, callback),
      callback,
    );
  }

  getFileExtension(fileName: string): string {
    const lastDot: number = fileName.lastIndexOf('.');
    return lastDot !== -1 ? fileName.slice(lastDot + 1).toLocaleLowerCase() : null;
  }

  async isFileSafe(file: File): Promise<boolean> {
    if (!file) return false;
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer.slice(0, 4));
    const header = Array.from(bytes)
      .map((byte) => byte.toString(16).toUpperCase().padStart(2, '0'))
      .join(' ');
    return !header.startsWith(WIN_EXE_DDL_STARTER) && header !== LINUX_ELF_STARTER;
  }

  doesAttachmentAlreadyExist(fileName: string, issueId: string): boolean {
    return this.query
      .getByFileName(fileName)
      .some((file) => file.issueId === issueId && !file.hidden)
  }

  private projectIdFromAttachment(attachmentId: ID): { projectId?: string } {
    const projectId = this.query.getEntity(attachmentId)?.projectId;
    return projectId ? { projectId } : {};
  }
}
